/**
 * Shipment lifecycle. `exception` is a recoverable side-state; `delivered` and `cancelled` are terminal.
 */
export const SHIPMENT_STATUSES = [
  "draft",
  "booked",
  "dispatched",
  "picked_up",
  "in_transit",
  "out_for_delivery",
  "delivered",
  "exception",
  "cancelled",
] as const;

export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number];

const RAW_TRANSITIONS: Record<ShipmentStatus, readonly ShipmentStatus[]> = {
  draft: ["booked", "cancelled"],
  booked: ["dispatched", "picked_up", "exception", "cancelled"],
  dispatched: ["picked_up", "exception", "cancelled"],
  picked_up: ["in_transit", "out_for_delivery", "delivered", "exception"],
  in_transit: ["out_for_delivery", "delivered", "exception"],
  out_for_delivery: ["delivered", "in_transit", "exception"],
  // From exception the carrier can resume movement, deliver, or (pre-pickup only, enforced by API) cancel.
  exception: ["booked", "dispatched", "picked_up", "in_transit", "out_for_delivery", "delivered", "cancelled"],
  delivered: [],
  cancelled: [],
};

/** Frozen so callers of allowedTransitions() cannot mutate the machine at runtime (run 128). */
const TRANSITIONS: Readonly<Record<ShipmentStatus, readonly ShipmentStatus[]>> = Object.freeze(
  Object.fromEntries(
    SHIPMENT_STATUSES.map((s) => [s, Object.freeze([...RAW_TRANSITIONS[s]])]),
  ) as Record<ShipmentStatus, readonly ShipmentStatus[]>,
);

/** Own-property lookup: unknown runtime strings (e.g. a bad DB value) get no exits instead of a TypeError. */
function exitsOf(s: ShipmentStatus): readonly ShipmentStatus[] {
  return isShipmentStatus(s) ? TRANSITIONS[s] : [];
}

/** Longest status label echoed into an error message; real statuses are far shorter. */
const MAX_STATUS_LABEL = 32;

/**
 * Safe, bounded label for a (possibly hostile) runtime status value. Template-literal coercion
 * throws a TypeError for Symbols and for objects whose toString throws (run 153), so non-strings
 * are described by type only and strings are stripped of control characters and clipped.
 */
function statusLabel(v: unknown): string {
  if (typeof v !== "string") return `<${v === null ? "null" : typeof v}>`;
  // eslint-disable-next-line no-control-regex
  const clean = v.replace(/[\u0000-\u001f\u007f-\u009f]/g, "");
  return clean.length > MAX_STATUS_LABEL ? `${clean.slice(0, MAX_STATUS_LABEL)}...` : clean;
}

export class InvalidTransitionError extends Error {
  override readonly name = "InvalidTransitionError";
  constructor(
    readonly from: ShipmentStatus,
    readonly to: ShipmentStatus,
  ) {
    super(`invalid shipment status transition: ${statusLabel(from)} -> ${statusLabel(to)}`);
  }
}

/** Longest carrier status code/description the tracking mappers inspect (run 153). */
export const MAX_CARRIER_STATUS_TEXT = 1024;

/**
 * Normalizes a carrier tracking status code or description from an untrusted payload: strings are
 * trimmed, upper-cased and clipped; finite numbers become their decimal text (carriers send numeric
 * codes); anything else (null, objects, Symbols, NaN) becomes "". Never throws.
 */
export function carrierStatusText(v: unknown): string {
  let s: string;
  if (typeof v === "string") s = v;
  else if (typeof v === "number" && Number.isFinite(v)) s = String(v);
  else return "";
  if (s.length > MAX_CARRIER_STATUS_TEXT) s = s.slice(0, MAX_CARRIER_STATUS_TEXT);
  return s.trim().toUpperCase();
}

export function isShipmentStatus(v: unknown): v is ShipmentStatus {
  return typeof v === "string" && (SHIPMENT_STATUSES as readonly string[]).includes(v);
}

export function allowedTransitions(from: ShipmentStatus): readonly ShipmentStatus[] {
  return exitsOf(from);
}

export function canTransition(from: ShipmentStatus, to: ShipmentStatus): boolean {
  return isShipmentStatus(to) && exitsOf(from).includes(to);
}

/** Returns `to` if allowed, else throws. Same-state updates are no-ops (idempotent tracking polls). */
export function transition(from: ShipmentStatus, to: ShipmentStatus): ShipmentStatus {
  if (from === to && isShipmentStatus(to)) return to;
  if (!canTransition(from, to)) throw new InvalidTransitionError(from, to);
  return to;
}

export function isTerminal(s: ShipmentStatus): boolean {
  return isShipmentStatus(s) && TRANSITIONS[s].length === 0;
}

/** Cancellation with the carrier is only possible before the freight is picked up. */
export function isCancellable(s: ShipmentStatus): boolean {
  return canTransition(s, "cancelled");
}
